import { useMemo } from 'react';
import { Area, AreaChart, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, TooltipContentProps, XAxis, YAxis } from 'recharts';
import { Card } from '../../../ui';
import { fmtDay, fmtDec, fmtInt, type PageHistoryRow } from './format';

interface Point {
  week: string;
  label: string;
  clicks: number;
  impressions: number;
  /** null for a week the page never showed — the line breaks there rather than diving to 0. */
  position: number | null;
}

const SYNC_ID = 'page-trend';
const axisTick = { fill: 'var(--text-faint)', fontSize: 10, fontFamily: 'var(--font-mono)' };

const addDays = (day: string, n: number) => {
  const [y, m, d] = day.split('-').map(Number);
  const next = new Date(y, m - 1, d + n);
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`;
};

/**
 * Weekly clicks, impressions and average position for one page. Three charts on one
 * time axis rather than one chart with three scales; hovering any of them moves all three.
 */
export default function PageTrendCharts({ history }: { history: PageHistoryRow[] }) {
  const points = useMemo<Point[]>(() => {
    if (history.length === 0) return [];
    const byWeek = new Map(history.map((h) => [h.period_start, h]));
    const weeks = [...byWeek.keys()].sort();
    const out: Point[] = [];
    // GSC omits weeks with no impressions; fill them so the axis stays continuous.
    for (let week = weeks[0]; week <= weeks[weeks.length - 1]; week = addDays(week, 7)) {
      const h = byWeek.get(week);
      out.push({
        week,
        label: fmtDay(week),
        clicks: h?.clicks ?? 0,
        impressions: h?.impressions ?? 0,
        position: h?.impressions ? Number(h.position) : null,
      });
    }
    return out;
  }, [history]);

  if (points.length === 0) {
    return (
      <Card eyebrow="Search Console" title="Weekly trend">
        <div style={{ padding: 'var(--space-8)', textAlign: 'center', color: 'var(--text-faint)', fontSize: 'var(--text-sm)' }}>
          No search history for this page yet.
        </div>
      </Card>
    );
  }

  const TrendTooltip = ({ active, payload }: TooltipContentProps<number, string>) => {
    if (!active || !payload?.length) return null;
    const p = payload[0].payload as Point;
    return (
      <div
        style={{
          padding: '10px 12px',
          background: 'var(--surface-raised)',
          border: '1px solid var(--border-hairline)',
          borderRadius: 'var(--radius-md)',
          boxShadow: 'var(--shadow-pop)',
        }}
      >
        <p className="t-eyebrow" style={{ margin: 0 }}>Week of {p.label}</p>
        <p className="t-num" style={{ margin: '4px 0 0', fontSize: 'var(--text-xs)', color: 'var(--text-body)', lineHeight: 1.6 }}>
          {fmtInt(p.clicks)} clicks · {fmtInt(p.impressions)} impressions
          <br />
          <span style={{ color: 'var(--text-muted)' }}>avg position {fmtDec(p.position)}</span>
        </p>
      </div>
    );
  };

  const xAxis = (showTicks: boolean) => (
    <XAxis
      dataKey="label"
      tick={showTicks ? axisTick : false}
      axisLine={{ stroke: 'var(--border-hairline)' }}
      tickLine={false}
      interval="preserveStartEnd"
      minTickGap={48}
      height={showTicks ? 24 : 6}
    />
  );
  const grid = <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" vertical={false} />;
  const margin = { top: 6, right: 12, left: 0, bottom: 0 };

  const panel = (label: string, chart: React.ReactElement, height: number) => (
    <div>
      <div className="t-eyebrow" style={{ marginBottom: '4px' }}>{label}</div>
      <ResponsiveContainer width="100%" height={height}>
        {chart}
      </ResponsiveContainer>
    </div>
  );

  return (
    <Card eyebrow="Search Console" title="Weekly trend">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
        {panel(
          'Clicks',
          <AreaChart data={points} syncId={SYNC_ID} margin={margin}>
            {grid}
            {xAxis(false)}
            <YAxis tick={axisTick} axisLine={false} tickLine={false} width={44} allowDecimals={false} />
            <Tooltip content={TrendTooltip} />
            <Area type="monotone" dataKey="clicks" stroke="var(--chart-primary)" strokeWidth={2} fill="var(--chart-primary)" fillOpacity={0.1} isAnimationActive={false} />
          </AreaChart>,
          110
        )}
        {panel(
          'Impressions',
          <AreaChart data={points} syncId={SYNC_ID} margin={margin}>
            {grid}
            {xAxis(false)}
            <YAxis tick={axisTick} axisLine={false} tickLine={false} width={44} allowDecimals={false} tickFormatter={(v: number) => (v >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : String(v))} />
            <Tooltip content={TrendTooltip} />
            <Area type="monotone" dataKey="impressions" stroke="var(--chart-primary)" strokeWidth={2} fill="var(--chart-primary)" fillOpacity={0.1} isAnimationActive={false} />
          </AreaChart>,
          110
        )}
        {panel(
          'Average position — higher is better',
          <LineChart data={points} syncId={SYNC_ID} margin={margin}>
            {grid}
            {xAxis(true)}
            <YAxis reversed domain={[1, 'auto']} tick={axisTick} axisLine={false} tickLine={false} width={44} allowDecimals={false} />
            <Tooltip content={TrendTooltip} />
            <Line type="monotone" dataKey="position" stroke="var(--chart-primary)" strokeWidth={2} dot={false} connectNulls={false} isAnimationActive={false} />
          </LineChart>,
          130
        )}
      </div>
    </Card>
  );
}
